import type { DemoMazeLevelDto, DemoSessionDto } from '@/types/api/demo';
import type { GameMazeDto } from '@/types/api/game';

import gameConfigJson from './data/game-config.json';

export type BoardDifficulty = 'easy' | 'medium' | 'hard';

export interface RawBoardJson {
  levelId: string;
  grid: number[][];
  rows: number;
  cols: number;
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
  isEnabled: boolean;
}

export interface SelectedBoard {
  raw: RawBoardJson;
  difficultyTier: BoardDifficulty;
  maxScore: number;
}

interface GameConfigLevel {
  boardCount: number;
  maxScore: number;
  level: { difficulty: BoardDifficulty };
}

interface GameConfigDemoLevel {
  boardCount: number;
}

interface GameConfigShape {
  timeLimit: number;
  maxTimeBonus: number;
  enableTimer: boolean;
  levels: GameConfigLevel[];
  demoLevels: GameConfigDemoLevel[];
}

const gameConfig = gameConfigJson as GameConfigShape;

export function getGameConfig(): GameConfigShape {
  return gameConfig;
}

function loadBoards(tier: BoardDifficulty): RawBoardJson[] {
  const modules = import.meta.glob<{ default: RawBoardJson }>(`./data/boards/*/*.json`, {
    eager: true,
  });
  const boards: RawBoardJson[] = [];
  for (const [path, mod] of Object.entries(modules)) {
    if (path.includes(`/boards/${tier}/`)) {
      boards.push(mod.default);
    }
  }
  return boards;
}

const boardsByTier: Record<BoardDifficulty, RawBoardJson[]> = {
  easy: loadBoards('easy'),
  medium: loadBoards('medium'),
  hard: loadBoards('hard'),
};

function loadDemoBoards(): RawBoardJson[] {
  const modules = import.meta.glob<{ default: RawBoardJson }>(`./data/boards/demo/*.json`, {
    eager: true,
  });
  return Object.values(modules).map((mod) => mod.default);
}

const demoBoards = loadDemoBoards();

function getLevelConfig(tier: BoardDifficulty): GameConfigLevel {
  const config = gameConfig.levels.find((l) => l.level.difficulty === tier);
  if (!config) {
    throw new Error(`No level config found for difficulty "${tier}"`);
  }
  return config;
}

/** Fisher-Yates partial shuffle; picks `count` items without replacement. */
function pickRandom<T>(arr: T[], count: number): T[] {
  const pool = [...arr];
  const result: T[] = [];
  const total = Math.min(count, pool.length);
  for (let i = 0; i < total; i += 1) {
    const index = Math.floor(Math.random() * pool.length);
    result.push(pool.splice(index, 1)[0]);
  }
  return result;
}

const TIER_PROGRESSION: Record<BoardDifficulty, BoardDifficulty[]> = {
  easy: ['easy', 'medium', 'hard'],
  medium: ['medium', 'hard'],
  hard: ['hard'],
};

/**
 * Builds the ordered, flat list of boards for a session. Choosing "easy" queues
 * easy -> medium -> hard boards back-to-back; the caller just walks an index through
 * this list, so no separate "advance difficulty" branch is needed anywhere else.
 */
export function selectSessionBoards(difficulty: BoardDifficulty): SelectedBoard[] {
  const tiers = TIER_PROGRESSION[difficulty];
  const selected: SelectedBoard[] = [];
  for (const tier of tiers) {
    const { boardCount, maxScore } = getLevelConfig(tier);
    const picked = pickRandom(boardsByTier[tier], boardCount);
    for (const raw of picked) {
      selected.push({ raw, difficultyTier: tier, maxScore });
    }
  }
  return selected;
}

export function mapRawBoardToGameMazeDto(
  board: SelectedBoard,
  opts: { roundNumber: number },
): GameMazeDto {
  const { raw } = board;
  return {
    sessionMazeId: crypto.randomUUID(),
    levelId: raw.levelId,
    roundNumber: opts.roundNumber,
    rows: raw.rows,
    cols: raw.cols,
    serverSeedHash: '',
    mazeGrid: raw.grid,
    startRow: raw.startRow,
    startCol: raw.startCol,
    endRow: raw.endRow,
    endCol: raw.endCol,
    shortestPathLength: 0,
    currentRow: raw.startRow,
    currentCol: raw.startCol,
    reachedEnd: false,
  };
}

function mapRawBoardToDemoLevel(raw: RawBoardJson, index: number): DemoMazeLevelDto {
  return {
    levelId: raw.levelId,
    levelName: `Demo ${index + 1}`,
    rows: raw.rows,
    cols: raw.cols,
    mazeGrid: raw.grid,
    startRow: raw.startRow,
    startCol: raw.startCol,
    endRow: raw.endRow,
    endCol: raw.endCol,
    shortestPathLength: 0,
  };
}

/** Builds a demo session locally from the bundled demo boards (no backend call). */
export function buildDemoSession(): DemoSessionDto {
  const boardCount = gameConfig.demoLevels[0]?.boardCount ?? demoBoards.length;
  const picked = pickRandom(demoBoards, boardCount);
  return {
    stageId: '',
    enableDemo: true,
    alreadySeen: false,
    levels: picked.map(mapRawBoardToDemoLevel),
  };
}
